import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { Loader2, UserPlus, Send, Check, X, UserCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full h-9 px-3 text-[13px] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]/60 focus:outline-none focus:ring-2 focus:ring-orange-500/40";
const labelCls = "block text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-1.5";

// Polls the pending-request count for the admin "Approve new clients" button.
// `enabled` keeps the hook inert for non-admins / other stages.
export function useClientRequestCount(enabled) {
  const [count, setCount] = useState(0);
  const refresh = useCallback(async () => {
    if (!enabled) { setCount(0); return; }
    try {
      const r = await axios.get(`${API}/client-requests?status=pending`);
      setCount(Array.isArray(r.data) ? r.data.length : 0);
    } catch { /* keep last value */ }
  }, [enabled]);
  useEffect(() => {
    refresh();
    if (!enabled) return undefined;
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh, enabled]);
  return [count, refresh];
}

// ── Requester side: small form — every active admin is notified ──
export function RequestClientModal({ onClose }) {
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [groupId, setGroupId] = useState("");
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  // Group choice (Phase 1b linkage): the approved client inherits the chosen
  // group tree's division automatically — drilling requests self-tag.
  useEffect(() => {
    axios.get(`${API}/client-groups`).then(r => setGroups(r.data || [])).catch(() => {});
  }, []);
  const groupOptions = groups.map(g => {
    const top = g.parent_id ? groups.find(p => p.id === g.parent_id) : null;
    return { id: g.id, name: top ? `${top.name} › ${g.name}` : g.name };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Enter the client name"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/client-requests`, { name: name.trim(), note: note.trim(), group_id: groupId });
      toast.success("Request sent", { description: "All admins have been notified — the client appears once approved." });
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to send request"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-sm" data-testid="request-client-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-orange-500" /> Request a new client</DialogTitle>
          <DialogDescription>All admins are notified — one of them approves it before it appears in the client list.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3.5">
          <div>
            <label className={labelCls}>Client name *</label>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Halliburton MENA" className={inputCls} data-testid="request-client-name" />
          </div>
          <div>
            <label className={labelCls}>Client group</label>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} className={`${inputCls} cursor-pointer`} data-testid="request-client-group">
              <option value="">No group / not sure</option>
              {groupOptions.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Note</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional — e.g. new PO arriving this week" className={inputCls} data-testid="request-client-note" />
          </div>
          <DialogFooter>
            <button type="button" onClick={onClose} className="px-3.5 py-2 text-[12.5px] font-medium rounded-lg border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]/60 transition-colors">Cancel</button>
            <button type="submit" disabled={saving}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[12.5px] font-medium rounded-lg bg-orange-600 hover:bg-orange-500 text-white transition-colors disabled:opacity-50"
              data-testid="request-client-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send request
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Admin side: approve / reject pending requests, with the current client
//    list shown for duplicate-spotting ──
export function ApproveClientsModal({ onClose, onDone }) {
  const [requests, setRequests] = useState(null);
  const [clients, setClients] = useState([]);
  const [rejecting, setRejecting] = useState(null);   // request id whose reason box is open
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(null);             // request id being processed

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/client-requests?status=pending`),
        axios.get(`${API}/clients`),
      ]);
      setRequests(Array.isArray(r.data) ? r.data : []);
      setClients(Array.isArray(c.data) ? c.data : []);
    } catch { setRequests([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (cr) => {
    setBusy(cr.id);
    try {
      await axios.post(`${API}/client-requests/${cr.id}/approve`);
      toast.success(`"${cr.name}" added to clients`, { description: `${cr.requested_by} has been notified.` });
      await load(); onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to approve"); }
    finally { setBusy(null); }
  };

  const reject = async (cr) => {
    setBusy(cr.id);
    try {
      await axios.post(`${API}/client-requests/${cr.id}/reject`, { reason: reason.trim() });
      toast.success(`Request "${cr.name}" rejected`, { description: `${cr.requested_by} has been notified${reason.trim() ? " with your reason" : ""}.` });
      setRejecting(null); setReason("");
      await load(); onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to reject"); }
    finally { setBusy(null); }
  };

  const fmtWhen = (iso) => {
    if (!iso) return "";
    const mins = Math.round((Date.now() - new Date(iso)) / 60000);
    if (mins < 60) return `${Math.max(1, mins)}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return `${Math.round(mins / 1440)}d ago`;
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="approve-clients-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-amber-500" /> Approve new clients
            {requests && <span className="text-[11px] font-normal text-[hsl(var(--muted-foreground))]">{requests.length} pending</span>}
          </DialogTitle>
          <DialogDescription>Approved clients join the master list immediately (colour editable later in client settings).</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-0.5">
          {requests === null ? (
            <div className="flex items-center gap-2 text-[12px] text-[hsl(var(--muted-foreground))] py-4"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</div>
          ) : requests.length === 0 ? (
            <div className="text-[12.5px] text-[hsl(var(--muted-foreground))] py-4 text-center">No pending requests — all caught up.</div>
          ) : requests.map((cr) => (
            <div key={cr.id} className="rounded-lg border border-[hsl(var(--border))] px-3 py-2.5" data-testid={`client-request-${cr.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold truncate">{cr.name}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 truncate">
                    {cr.requested_by} · {fmtWhen(cr.created_at)}{cr.note ? ` · “${cr.note}”` : ""}
                  </div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button type="button" disabled={busy === cr.id} onClick={() => approve(cr)}
                    className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/8 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/15 transition-colors disabled:opacity-50"
                    data-testid={`approve-client-${cr.id}`}>
                    {busy === cr.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                  </button>
                  <button type="button" disabled={busy === cr.id}
                    onClick={() => { setRejecting(rejecting === cr.id ? null : cr.id); setReason(""); }}
                    className="inline-flex items-center gap-1 text-[11.5px] font-medium px-2.5 py-1.5 rounded-md border border-red-500/30 text-red-600 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                    data-testid={`reject-client-${cr.id}`}>
                    <X className="w-3 h-3" /> Reject
                  </button>
                </div>
              </div>
              {rejecting === cr.id && (
                <div className="flex items-center gap-2 mt-2">
                  <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); reject(cr); } }}
                    placeholder="reason — sent back to the requester"
                    className={`${inputCls} h-8 text-[12px]`} data-testid={`reject-reason-${cr.id}`} />
                  <button type="button" disabled={busy === cr.id} onClick={() => reject(cr)}
                    className="shrink-0 text-[11.5px] font-medium px-2.5 py-1.5 rounded-md bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50"
                    data-testid={`reject-confirm-${cr.id}`}>
                    Send
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="border-t border-[hsl(var(--border))]/60 pt-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[hsl(var(--muted-foreground))] mb-2">
            Current clients ({clients.length}) — check for duplicates
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-[18vh] overflow-y-auto">
            {clients.map((c) => (
              <span key={c.id} className="text-[11px] px-2 py-0.5 rounded-full bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))]">{c.name}</span>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
